import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { A11y, EffectCoverflow, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper/types';

import { useOptions, useTranslatedEntity, useWindowSize } from '../../hooks';
import { actions } from '../../models';
import { useAppDispatch, useAppSelector } from '../../store';
import type { NomineeType, PollType } from '../../types';
import ErrorBoundary from '../../utils/ErrorBoundary';
import LocalStorageClient from '../../utils/LocalStorageClient';
import { trackSectionNomineeImpression } from '../../utils/trackUtils';
import Nominee from '../Nominee';

import * as style from '../../pages/GOTx/MainGOTx/style.module.scss';

export const getIndexFromNomineeId = (id: string, nominees?: NomineeType[]) => {
  const nominee = nominees?.find((n: NomineeType) => n.id === id);
  return nominee && nominees?.indexOf(nominee);
};

const PollCarousel = ({ poll, autoplay = true }: { poll?: PollType | null; autoplay?: boolean }) => {
  const dispatch = useAppDispatch();
  const { width, orientation } = useWindowSize();
  const isMobile = useMemo(
    () => width < 768 || (width < 1024 && orientation.startsWith('landscape')),
    [width, orientation],
  );
  const sponsor = useAppSelector((state) => state.sponsors?.sponsor);
  const user = useAppSelector((state) => state.user);
  const [nominee, setNominee] = useState(null);

  const { title } = useTranslatedEntity(poll);

  const params: { nomineeId?: string } = useParams();
  const [length, setLength] = useState(poll?.nominees.length ?? 0);
  const carouselEffect = useMemo(() => (isMobile ? 'slide' : 'coverflow'), [isMobile]);
  const { appType, competitionBanner } = useOptions();

  const titleOn = appType !== 'gots';

  const trackedNominees = useRef<Set<string>>(new Set());

  const sendNomineeImpressionTracking = useCallback(
    (nominee: NomineeType) => {
      const nomineeId = nominee.id;
      if (trackedNominees.current.has(nomineeId)) return;

      trackSectionNomineeImpression({
        poll,
        nominee,
        appType,
        competition: window.competitionTracking ?? competitionBanner,
        sponsor,
        userVoted: LocalStorageClient.userVoted(poll, user),
      });

      trackedNominees.current.add(nomineeId);
    },
    [poll, appType, competitionBanner, sponsor, user],
  );

  const handleSwiperImpressionTracking = (s: SwiperType) => {
    const nominee = poll.nominees[s.realIndex];
    sendNomineeImpressionTracking(nominee);
  };

  const onSwiperHandler = (s: SwiperType) => setNominee(poll.nominees[s.realIndex]);

  const handleClick = (swiper: SwiperType) => {
    if (swiper.clickedIndex < swiper.activeIndex) {
      swiper.slidePrev();
    } else if (swiper.clickedIndex > swiper.activeIndex) {
      swiper.slideNext();
    }
  };

  useEffect(() => {
    poll && dispatch(actions.user.setUserVoted(poll));
    setLength(poll?.nominees.length ?? 0);
  }, [poll, dispatch]);

  useEffect(() => {
    if (!nominee) return;
    sendNomineeImpressionTracking(nominee);
  }, [nominee, sendNomineeImpressionTracking]);

  const initialSlide = useMemo(
    () => (params?.nomineeId ? getIndexFromNomineeId(params.nomineeId, poll?.nominees) : 0),
    [params, poll?.nominees],
  );

  return (
    <ErrorBoundary>
      {poll?.nominees && (
        <>
          {titleOn && !!title && (
            <h2 data-testid='carousel' className={style.gotxCarouselTitle}>
              {title}
            </h2>
          )}
          <div className={style.gotxCarouselItems}>
            <Swiper
              data-testid='carousel'
              initialSlide={initialSlide}
              loop={poll?.nominees?.length > 2}
              modules={[Navigation, EffectCoverflow, A11y, Pagination]}
              keyboard={false}
              centeredSlides
              navigation
              watchSlidesProgress
              pagination={{
                clickable: true,
              }}
              slidesPerGroup={1}
              spaceBetween={10}
              effect={carouselEffect}
              slidesPerView={isMobile ? 1.2 : 1.53}
              breakpoints={{
                960: {
                  slidesPerView: 'auto',
                },
              }}
              coverflowEffect={{
                rotate: 0,
                stretch: 80,
                depth: 200,
                modifier: 1,
                slideShadows: false,
              }}
              onClick={handleClick}
              onSwiper={onSwiperHandler}
              onRealIndexChange={handleSwiperImpressionTracking}>
              {poll.nominees.slice(0, length).map((n: NomineeType) => (
                <SwiperSlide key={`item_${n.id}`}>
                  <Nominee poll={poll} nominee={n} autoplay={autoplay} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </>
      )}
    </ErrorBoundary>
  );
};

export default PollCarousel;
